import { useSyncExternalStore } from 'react';
import { DEFAULT_CANVAS_FORMAT_ID } from '../canvasFormats';
import { createInitialProjectState, PROJECT_STORAGE_KEY, PROJECT_STORAGE_VERSION } from '../shared/projectDefaults';

const channelName = 'lower-thirds-project';
const subscribers = new Set();
let channel = null;
let snapshot = null;
let snapshotRaw = null;
let snapshotSignature = null;

const buildProjectSnapshotSignature = (project) => JSON.stringify({
  people: project?.people || [],
  selectedPersonId: project?.selectedPersonId || null,
  activePresetId: project?.activePresetId || null,
  customPresets: project?.customPresets || [],
  config: project?.config || null,
  aspectRatioId: project?.aspectRatioId || null,
  fps: project?.fps || null,
});

const notify = () => {
  subscribers.forEach((listener) => listener());
};

const cacheSnapshot = (nextSnapshot, raw = snapshotRaw) => {
  snapshot = nextSnapshot;
  snapshotRaw = raw;
  snapshotSignature = buildProjectSnapshotSignature(nextSnapshot);
};

const ensureChannel = () => {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  channel = new BroadcastChannel(channelName);
  channel.onmessage = (event) => {
    if (!event?.data || typeof event.data !== 'object') return;
    if (event.data.type !== 'project:update' || !event.data.project) return;
    const nextSnapshot = sanitizeProjectState(event.data.project);
    const nextSignature = buildProjectSnapshotSignature(nextSnapshot);
    if (nextSignature === snapshotSignature) return;
    snapshot = nextSnapshot;
    snapshotSignature = nextSignature;
    notify();
  };
  return channel;
};

const sanitizePeople = (people) => (
  Array.isArray(people) && people.length
    ? people.map((person, index) => ({
      id: typeof person?.id === 'string' && person.id.trim() ? person.id : `person-${index + 1}`,
      name: typeof person?.name === 'string' ? person.name : '',
      title: typeof person?.title === 'string' ? person.title : '',
      mirrored: person?.mirrored === true,
      genericTextByPresetId: person?.genericTextByPresetId && typeof person.genericTextByPresetId === 'object'
        ? person.genericTextByPresetId
        : {},
    }))
    : createInitialProjectState().people
);

export const sanitizeProjectState = (rawProject) => {
  const fallback = createInitialProjectState();
  const people = sanitizePeople(rawProject?.people);
  const selectedPersonId = people.some((person) => person.id === rawProject?.selectedPersonId)
    ? rawProject.selectedPersonId
    : people[0]?.id || null;
  return {
    version: PROJECT_STORAGE_VERSION,
    revision: Number.isFinite(rawProject?.revision) ? rawProject.revision : fallback.revision,
    updatedAt: Number.isFinite(rawProject?.updatedAt) ? rawProject.updatedAt : Date.now(),
    people,
    selectedPersonId,
    activePresetId: typeof rawProject?.activePresetId === 'string' && rawProject.activePresetId.trim()
      ? rawProject.activePresetId
      : fallback.activePresetId,
    customPresets: Array.isArray(rawProject?.customPresets) ? rawProject.customPresets : fallback.customPresets,
    config: rawProject?.config && typeof rawProject.config === 'object' ? rawProject.config : fallback.config,
    aspectRatioId: typeof rawProject?.aspectRatioId === 'string' && rawProject.aspectRatioId.trim()
      ? rawProject.aspectRatioId
      : DEFAULT_CANVAS_FORMAT_ID,
    fps: Number.isFinite(rawProject?.fps) ? rawProject.fps : fallback.fps,
  };
};

const readStoredProjectState = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    const nextSnapshot = createInitialProjectState();
    cacheSnapshot(nextSnapshot, null);
    return nextSnapshot;
  }
  try {
    const raw = window.localStorage.getItem(PROJECT_STORAGE_KEY);
    if (!raw) {
      const nextSnapshot = createInitialProjectState();
      cacheSnapshot(nextSnapshot, null);
      return nextSnapshot;
    }
    const parsed = JSON.parse(raw);
    const nextSnapshot = sanitizeProjectState(parsed);
    cacheSnapshot(nextSnapshot, raw);
    return nextSnapshot;
  } catch {
    const nextSnapshot = createInitialProjectState();
    cacheSnapshot(nextSnapshot, null);
    return nextSnapshot;
  }
};

export const getProjectSnapshot = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const raw = window.localStorage.getItem(PROJECT_STORAGE_KEY);
    if (raw == null) {
      const fallbackSnapshot = createInitialProjectState();
      const fallbackSignature = buildProjectSnapshotSignature(fallbackSnapshot);
      if (snapshotRaw !== null || snapshotSignature !== fallbackSignature) {
        snapshotRaw = null;
        snapshot = fallbackSnapshot;
        snapshotSignature = fallbackSignature;
      }
    } else if (raw !== snapshotRaw) {
      snapshot = readStoredProjectState();
    }
  }
  if (!snapshot) snapshot = readStoredProjectState();
  return snapshot;
};

const persistProjectState = (project) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const raw = JSON.stringify(project);
    window.localStorage.setItem(PROJECT_STORAGE_KEY, raw);
    snapshotRaw = raw;
  } catch {
    // Ignore storage failures; the editor already surfaces preset quota issues.
  }
};

export const replaceProjectState = (nextProject, options = {}) => {
  const currentSnapshot = getProjectSnapshot();
  const nextSnapshot = sanitizeProjectState({
    ...nextProject,
    revision: Number.isFinite(nextProject?.revision)
      ? nextProject.revision
      : (currentSnapshot.revision + 1),
    updatedAt: Date.now(),
  });
  const nextSignature = buildProjectSnapshotSignature(nextSnapshot);
  if (snapshot && nextSignature === snapshotSignature) {
    return snapshot;
  }
  snapshot = nextSnapshot;
  snapshotSignature = nextSignature;
  persistProjectState(nextSnapshot);
  if (options.broadcast !== false) {
    ensureChannel()?.postMessage({ type: 'project:update', project: nextSnapshot });
  }
  notify();
  return nextSnapshot;
};

export const updateProjectState = (updater, options) => {
  const current = getProjectSnapshot();
  const nextValue = typeof updater === 'function' ? updater(current) : updater;
  return replaceProjectState(nextValue, options);
};

export const subscribeProjectStore = (listener) => {
  subscribers.add(listener);
  ensureChannel();

  const handleStorage = (event) => {
    if (event.key !== PROJECT_STORAGE_KEY) return;
    if (!event.newValue) {
      const nextSnapshot = createInitialProjectState();
      const nextSignature = buildProjectSnapshotSignature(nextSnapshot);
      if (event.newValue === snapshotRaw && nextSignature === snapshotSignature) return;
      cacheSnapshot(nextSnapshot, null);
      notify();
      return;
    }
    try {
      const nextSnapshot = sanitizeProjectState(JSON.parse(event.newValue));
      const nextSignature = buildProjectSnapshotSignature(nextSnapshot);
      if (event.newValue === snapshotRaw && nextSignature === snapshotSignature) return;
      cacheSnapshot(nextSnapshot, event.newValue);
      notify();
    } catch {
      // Ignore parse errors from other tabs.
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorage);
  }

  return () => {
    subscribers.delete(listener);
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorage);
    }
  };
};

export const useProjectSnapshot = () => (
  useSyncExternalStore(subscribeProjectStore, getProjectSnapshot, createInitialProjectState)
);
